import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { FeatureToggleEnum, useFeatureTogglesContext } from './FeatureTogglesContext';
import { useFlipperExperimentBucketAssignmentQueryLazyQuery, useMembershipQuery } from '../generated/graphql';

interface AiTextExperimentContextType {
  showAiButton: boolean;
}

const AiTextExperimentContext = createContext<AiTextExperimentContextType | undefined>(undefined);

interface AiTextExperimentProviderProps {
  children: ReactNode;
}

export const AiTextExperimentProvider: React.FC<AiTextExperimentProviderProps> = ({ children }) => {
  const [showAiButton, setShowAiButton] = useState<boolean>(false);
  const { data: memberData } = useMembershipQuery();
  const { isEnabled } = useFeatureTogglesContext();
  const aiButtonToggleEnabled = isEnabled(FeatureToggleEnum.AiTextButton);
  const [
    callFlipperBucketAssignment,
    { data: flipperData, loading: flipperLoading, error: flipperError },
  ] = useFlipperExperimentBucketAssignmentQueryLazyQuery();

  const memberId = memberData?.member?.id;

  // determine the correct value for showAiButton
  useEffect(() => {
    const calculateShowAiButton = async () => {
      //unleash toggle overrides flipper experiment
      if (aiButtonToggleEnabled) {
        setShowAiButton(true);
        return;
      }

      //skip if member or flipper are loading
      if (!memberId || flipperLoading) return;

      // only make the flipper query once, skip if we're waiting on data or have already
      // received an error or success response
      if (!flipperData && !flipperLoading && !flipperError) {
        await callFlipperBucketAssignment({
          variables: {
            flipperExperimentBucketExperimentId: process.env.REACT_APP_FLIPPER_WWS_AI_TEXT_BUTTON_EXP_ID as string,
            flipperExperimentBucketId: memberId,
          },
        });
      }

      // check flipper bucket status
      if (!flipperError && flipperData?.flipperExperimentBucketAssignment) {
        if (flipperData.flipperExperimentBucketAssignment.variant_name === 'variant') {
          setShowAiButton(true);
          return;
        }
      }
    };

    calculateShowAiButton();
  }, [memberId, aiButtonToggleEnabled, flipperData, flipperLoading, flipperError, callFlipperBucketAssignment]);

  return <AiTextExperimentContext.Provider value={{ showAiButton }}>{children}</AiTextExperimentContext.Provider>;
};

export const useAiTextExperimentContext = (): AiTextExperimentContextType => {
  const context = useContext(AiTextExperimentContext);
  if (context === undefined) {
    throw new Error('useAiTextExperimentContext must be used within an AiTextExperimentProvider');
  }
  return context;
};
